import { ApiError, Card, CardContent, CardHeader, CardTitle } from '@dataroom/shared';
import { useClassificationLearning, useSetClassificationLearning } from '@/api/hooks';

function plainError(error: unknown): string | null {
  if (error instanceof ApiError) return error.message;
  return error ? 'Request failed.' : null;
}

/**
 * Cross-customer classification learning (ADR-0020 phase 3) on Settings —
 * owner-only. On by default: when someone at the firm files a document, the
 * shape of its file name (names, numbers and dates removed) and the standard
 * checklist category it was filed under help file similar documents for every
 * customer, and the firm's own documents are filed with what other customers
 * agreed on. Turning it off withdraws the firm's contribution at once.
 */
export function ClassificationLearningCard() {
  const setting = useClassificationLearning();
  const save = useSetClassificationLearning();
  const on = setting.data?.shareWithAllCustomers ?? true;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Learning from all customers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          We collect anonymized usage habits to improve the product for everyone. When someone at
          your firm files a document, only the shape of its file name (for example &ldquo;
          {'{company} {year}'} tax return&rdquo;, with names, numbers and dates removed) and the
          standard checklist category it went into are shared — never the document, its name, your
          own categories, or who you are. In return, your documents are filed with what other firms
          agreed on.
        </p>
        {setting.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : setting.error ? (
          <p className="text-sm text-destructive">{plainError(setting.error)}</p>
        ) : (
          <label className="flex w-fit items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={on}
              disabled={save.isPending}
              onChange={(e) => {
                save.reset();
                save.mutate(e.target.checked);
              }}
            />
            Share anonymized filing patterns and learn from all customers
          </label>
        )}
        {save.error ? <p className="text-sm text-destructive">{plainError(save.error)}</p> : null}
      </CardContent>
    </Card>
  );
}
